import { useEffect, useMemo, useRef, useState } from "react";
import { track } from "@/lib/api";
import { makeEmpty, safetyKinds } from "@/lib/journeyEngine";
import { JourneyIntro } from "./JourneyIntro";
import { JourneyFrame } from "./JourneyFrame";
import { QuestionStep } from "./QuestionStep";
import { SafetyPanel, SafetyDisclaimer } from "./SafetyPanel";
import { JourneyResults } from "./JourneyResults";

export const JourneyRunner = ({ journey }) => {
  const ids = useMemo(() => [...(journey.safety_question ? ["safety"] : []), ...journey.questions.map((q) => q.id)], [journey]);
  const empty = useMemo(() => makeEmpty(journey), [journey]);
  const steps = ["intro", ...ids, "results"];
  const [i, setI] = useState(0);
  const [a, setA] = useState(empty);
  const headingRef = useRef(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    window.scrollTo(0, 0);
    headingRef.current?.focus();
  }, [i]);

  const step = steps[i];
  const last = steps.length - 1;
  const restart = () => { setA(empty); setI(0); };
  const start = (to) => { track("journey_start", journey.slug); setA(empty); setI(to); };

  if (step === "intro") return <JourneyIntro journey={journey} headingRef={headingRef} onStart={() => start(1)} onGuidanceNow={() => start(last)} />;
  if (step === "results") return <JourneyResults journey={journey} answers={a} headingRef={headingRef} onRestart={restart} onBack={() => setI(last - 1)} />;

  const isSafety = step === "safety";
  const q = isSafety ? { id: "safety", type: "single", ...journey.safety_question } : journey.questions.find((x) => x.id === step);
  const kinds = safetyKinds(journey, a, [step]);
  const panels = journey.safety_panels;
  return (
    <JourneyFrame
      step={i}
      total={ids.length}
      onBack={() => setI(i - 1)}
      onContinue={() => setI(i + 1)}
      continueLabel={kinds.includes("yes") ? panels.yes.continue_label : "Continue"}
      continueDisabled={isSafety && !a.safety}
      onSkip={isSafety ? null : () => { setA((s) => ({ ...s, [step]: empty[step] })); setI(i + 1); }}
    >
      <QuestionStep q={q} value={a[step]} onChange={(v) => setA((s) => ({ ...s, [step]: v }))} headingRef={headingRef} />
      {kinds.map((k) => <SafetyPanel key={k} kind={k} panel={panels[k]} />)}
      {(isSafety || kinds.length > 0) && <SafetyDisclaimer text={panels.disclaimer} />}
    </JourneyFrame>
  );
};
